import React, { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import GuionTableEditor from './GuionTableEditor';
import { CREATIVE_TIPOS } from './AdsCreativeParts';
import { adsApprovalsAPI } from '../lib/api';

const EMPTY = { nombre: '', tipo: 'imagen', drive_url: '', copy_text: '', guion_table: [] };

export const CreativeEditorDialog = ({ open, batchId, creative, onClose, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(creative ? { ...EMPTY, ...creative } : EMPTY); }, [open, creative]);
  const set = (p) => setF((prev) => ({ ...prev, ...p }));

  const save = async () => {
    if (!f.nombre.trim()) { toast.error('Ponle un nombre al creativo'); return; }
    setSaving(true);
    const data = { nombre: f.nombre.trim(), tipo: f.tipo, drive_url: f.drive_url.trim(), copy_text: f.copy_text, guion_table: f.guion_table || [] };
    try {
      await (creative ? adsApprovalsAPI.updateCreative(creative.id, data) : adsApprovalsAPI.addCreative(batchId, data));
      onSaved();
      onClose();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="creative-editor-dialog">
        <DialogHeader><DialogTitle>{creative ? 'Editar creativo' : 'Nuevo creativo'}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2 space-y-1.5"><Label>Nombre / identificador</Label>
            <Input value={f.nombre} onChange={(e) => set({ nombre: e.target.value })} placeholder="Ej.: CAP_V1_Testimonio" data-testid="creative-name-input" /></div>
          <div className="space-y-1.5"><Label>Tipo</Label>
            <div className="flex gap-1.5">
              {Object.entries(CREATIVE_TIPOS).map(([v, l]) => (
                <button key={v} type="button" onClick={() => set({ tipo: v })} data-testid={`creative-type-${v}`}
                  className={`flex-1 h-9 rounded-md border text-sm transition-colors ${f.tipo === v ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}>{l}</button>
              ))}
            </div></div>
          <div className="sm:col-span-3 space-y-1.5"><Label>Enlace a Drive del archivo</Label>
            <Input value={f.drive_url} onChange={(e) => set({ drive_url: e.target.value })} placeholder="https://drive.google.com/file/d/..." data-testid="creative-drive-input" />
            <p className="text-xs text-muted-foreground">Compártelo con el cliente («Cualquiera con el enlace») para que vea la vista previa.</p></div>
          <div className="sm:col-span-3 space-y-1.5"><Label>Copy del anuncio</Label>
            <Textarea value={f.copy_text} onChange={(e) => set({ copy_text: e.target.value })} rows={4} data-testid="creative-copy-input" /></div>
        </div>
        {f.tipo === 'video' && (
          <div className="space-y-1.5 min-w-0"><Label>Guion</Label>
            <div className="overflow-x-auto"><GuionTableEditor rows={f.guion_table || []} onChange={(rows) => set({ guion_table: rows })} /></div></div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="creative-save-btn">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Guardar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
